/**
 * Planeten-Selektor unten links.
 * Runder Planet-Button mit Holo-Panel + Kamera-Pan per Drag.
 */
import type { Engine } from '../core/Engine';
import type { GameModule } from '../core/GameModule';
import type { Camera } from '../renderer/Camera';
import type { PlanetSummary } from '../world/PlayerWorldTypes';
import { axialToWorld } from '../math/HexMath';
import { PlanetBtnPan } from './PlanetBtnPan';
import {
  ICONS, B, BRD, buildPlanetButton, buildPlanetPanel, populatePlanetList,
} from './PlanetListPanelDOM';
import { getSettings } from './SettingsPanel';

const ANIM_MS = 300;

export class PlanetListPanel implements GameModule {
  readonly name = 'PlanetListPanel';
  private _camera: Camera;
  private _hexRadius: number;
  private _planets: PlanetSummary[];
  private _selectedIdx = 0;
  private _open = false;
  private _anim: Animation | null = null;
  private _pan!: PlanetBtnPan;

  private _root!: HTMLDivElement;
  private _btn!: HTMLDivElement;
  private _iconEl!: HTMLSpanElement;
  private _panel!: HTMLDivElement;
  private _listEl!: HTMLDivElement;
  private _onOutside = (e: PointerEvent): void => {
    if (this._open && !this._root.contains(e.target as Node)) this._close();
  };

  constructor(camera: Camera, hexRadius: number, planets: PlanetSummary[]) {
    this._camera = camera;
    this._hexRadius = hexRadius;
    this._planets = planets;
  }

  init(engine: Engine): void {
    this._root = document.createElement('div');
    Object.assign(this._root.style, {
      position: 'fixed', bottom: '12px', left: '12px', zIndex: '9998',
    });
    this._iconEl = document.createElement('span');
    this._listEl = document.createElement('div');
    this._btn = buildPlanetButton(this._iconEl);
    this._panel = buildPlanetPanel(this._listEl);
    this._pan = new PlanetBtnPan(this._btn, () => {
      this._open ? this._close() : this._openList();
    });
    this._pan.setIconEl(this._iconEl);
    this._btn.addEventListener('pointerenter', () => {
      if (!this._pan?.panning) this._btn.style.borderColor = B + '0.6)';
    });
    this._btn.addEventListener('pointerleave', () => {
      if (!this._pan?.panning) this._btn.style.borderColor = BRD;
    });
    this._root.append(this._btn, this._panel);
    document.body.appendChild(this._root);
    document.addEventListener('pointerdown', this._onOutside);
    this._updateButton();
    this._populateList();

    engine.events.on('planet:hub-opened', () => { this._root.style.display = 'none'; });
    engine.events.on('planet:hub-closed', () => { this._root.style.display = ''; });
  }

  update(_dt: number): void {
    if (!this._pan.panning || this._pan.magnitude < 0.01) return;
    const camH = this._camera.position.data[1];
    const speed = camH * getSettings().panSpeed * this._pan.magnitude * _dt;
    this._camera.target.data[0] += this._pan.dirX * speed;
    this._camera.target.data[2] += this._pan.dirY * speed;
  }

  dispose(): void {
    document.removeEventListener('pointerdown', this._onOutside);
    this._pan.dispose();
    this._root.remove();
  }

  setPlanets(planets: PlanetSummary[]): void {
    this._planets = planets;
    this._selectedIdx = Math.min(this._selectedIdx, Math.max(0, planets.length - 1));
    this._updateButton();
    this._populateList();
  }

  private _updateButton(): void {
    const p = this._planets[this._selectedIdx];
    this._iconEl.textContent = p ? (ICONS[p.planetType] ?? '\u{1FA90}') : '\u{1FA90}';
  }

  private _populateList(): void {
    populatePlanetList(this._listEl, this._planets, this._selectedIdx, (idx) => this._selectPlanet(idx));
  }

  private _selectPlanet(idx: number): void {
    this._selectedIdx = idx;
    const p = this._planets[idx];
    const [wx, wz] = axialToWorld(p.axialQ, p.axialR, this._hexRadius);
    this._camera.target.set(wx, 0, wz);
    this._updateButton();
    this._close();
  }

  private _openList(): void {
    this._anim?.cancel();
    this._open = true;
    this._populateList();
    this._panel.style.display = '';
    this._anim = this._panel.animate([
      { transform: 'scale(0)', opacity: 0 },
      { transform: 'scale(1.03)', opacity: 1, offset: 0.75 },
      { transform: 'scale(1)', opacity: 1 },
    ], { duration: ANIM_MS, easing: 'cubic-bezier(0.22, 0.85, 0.32, 1.1)', fill: 'forwards' });
  }

  private _close(): void {
    this._anim?.cancel();
    this._open = false;
    this._anim = this._panel.animate([
      { transform: 'scale(1)', opacity: 1 },
      { transform: 'scale(0)', opacity: 0 },
    ], { duration: ANIM_MS * 0.5, easing: 'ease-in', fill: 'forwards' });
    this._anim.onfinish = () => { this._panel.style.display = 'none'; this._anim = null; };
  }
}
